// src/pages/ChatPage.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  limit,
  startAfter,
  where,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

// Storage for uploads
import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from 'firebase/storage';

const GLOBAL_CHAT_ID = 'global';
const makeDmChatId = (a, b) => (a < b ? `${a}_${b}` : `${b}_${a}`);

// ---------- Design Tokens (CSS-in-JS via <style>) ----------
const DesignCSS = () => (
  <style>{`
    :root{
      --bg: #f6f7fb;
      --bg-grad: radial-gradient(1200px 600px at 10% -10%, #eef2ff 0%, transparent 60%), radial-gradient(900px 500px at 110% 10%, #ecfeff 0%, transparent 60%);
      --card: #ffffff;
      --muted: #6b7280;
      --text: #0f172a;
      --border: #e5e7eb;
      --primary: #2563eb;
      --primary-600: #1d4ed8;
      --primary-50: #eff6ff;
      --success-50:#ecfdf5;
      --success-700:#047857;
      --accent-50:#fff7ed;
      --accent-600:#9a3412;
      --warning:#f59e0b;
      --danger-50:#fff1f2;
      --danger-600:#be123c;
      --shadow: 0 10px 30px rgba(2, 6, 23, 0.06);
      --shadow-soft: 0 6px 18px rgba(2, 6, 23, 0.05);
      --radius-lg: 14px;
      --radius-md: 10px;
      --radius-sm: 8px;
    }

    .chat-root{
      background: var(--bg);
      background-image: var(--bg-grad);
      min-height: calc(100vh - 64px);
      color: var(--text);
      font-synthesis-weight: none;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    .card{
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow);
    }

    .header-bar{
      position: sticky;
      top: 0;
      backdrop-filter: saturate(120%) blur(6px);
      z-index: 5;
    }

    .ghost-btn{
      border: 1px solid var(--border);
      background: #fff;
      border-radius: 10px;
      padding: 8px 12px;
      transition: 140ms ease;
    }
    .ghost-btn:hover{
      transform: translateY(-1px);
      box-shadow: var(--shadow-soft);
    }
    .ghost-btn:active{
      transform: translateY(0);
    }

    .primary-btn{
      background: var(--primary);
      border: 1px solid var(--primary);
      color: #fff;
      border-radius: 10px;
      padding: 10px 14px;
      font-weight: 600;
      transition: 140ms ease;
    }
    .primary-btn:hover{ background: var(--primary-600); }

    .tab{
      border: 1px solid var(--border);
      background: #fff;
      border-radius: 10px;
      padding: 8px 12px;
      transition: 140ms ease;
    }
    .tab:hover{ box-shadow: var(--shadow-soft); }
    .tab--active{
      background: #0f172a; color:#fff; border-color:#0f172a;
      box-shadow: var(--shadow-soft);
    }

    .list-item{
      border-radius: 12px;
      transition: 140ms ease;
    }
    .list-item:hover{
      background: #f8fafc;
    }
    .list-item--active{
      background: #eef2ff; color:#312e81;
      outline: 1px solid #c7d2fe;
    }

    /* Messages area */
    .messages{
      overflow: auto;
      padding: 14px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .day-chip{
      font-size: 12px;
      color: var(--muted);
      background: #f9fafb;
      border: 1px solid var(--border);
      padding: 4px 10px;
      border-radius: 999px;
      box-shadow: var(--shadow-soft);
    }

    /* Bubbles with tails */
    .bubble{
      max-width: min(78%, 720px);
      padding: 10px 12px;
      border-radius: 16px;
      background: #f3f4f6;
      position: relative;
      word-break: break-word;
      box-shadow: var(--shadow-soft);
    }
    .bubble::after{
      content:"";
      position:absolute; bottom:-2px; left:-5px;
      width:10px; height:10px;
      background: inherit;
      border-bottom-left-radius: 12px;
      transform: rotate(45deg);
      box-shadow: 2px 2px 0 0 rgba(0,0,0,0.02);
    }
    .bubble--mine{
      margin-left: auto;
      background: #e0ecff;
    }
    .bubble--mine::after{
      left:auto; right:-5px; transform: rotate(-45deg);
    }

    .meta{
      font-size: 11px;
      color: var(--muted);
      margin-top: 6px;
      display: flex;
      gap: 8px;
      align-items: center;
      flex-wrap: wrap;
    }
    .hint{ font-size: 11px; color:#94a3b8; margin-top: 4px; }

    .chip{
      display:inline-flex; align-items:center; gap:6px;
      font-size:12px; padding: 3px 8px; border-radius:999px;
      background: #f3f4f6; color:#374151; border:1px solid var(--border);
    }
    .chip--seen{ background: var(--success-50); color: var(--success-700); border-color:#d1fae5; }
    .chip--pinned{ background: var(--accent-50); color: var(--accent-600); border-color:#fed7aa; }

    .progress{
      height: 10px; background:#eef2ff; border-radius:999px; overflow:hidden; border:1px solid #e5e7eb;
    }
    .progress > span{
      display:block; height:100%; background: var(--primary);
      width:0%;
      transition: width 220ms ease;
    }

    .drop-zone{
      border: 1px solid var(--border);
      border-radius: 12px;
      transition: 140ms ease;
    }
    .drop-zone--drag{
      border: 2px dashed var(--primary);
      background: #f8fbff;
    }

    /* Scrollbar refinement */
    .messages::-webkit-scrollbar{ width: 10px; }
    .messages::-webkit-scrollbar-thumb{
      background: #e5e7eb;
      border-radius: 999px;
      border: 2px solid #ffffffaa;
    }
  `}</style>
);

// ---------- Inline Styles (layout grid numbers, spacing) ----------
const S = {
  outer: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: 14,
    height: 'calc(100vh - 80px)',
    padding: 16,
  },
  outerMobile: { gridTemplateColumns: '1fr', padding: 10, height: 'calc(100vh - 64px)' },
  sidebar: { padding: 12, overflow: 'auto', minHeight: 0 },
  content: {
    display: 'grid',
    gridTemplateRows: '64px auto 20px auto 64px',
    minHeight: 0,
  },
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: 8,
    padding: '0 14px',
    borderBottom: '1px solid #e5e7eb',
  },
  headerTitle: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 },
  titleText: { fontWeight: 800, letterSpacing: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  sectionTitle: { fontWeight: 800, margin: '6px 0 10px', letterSpacing: 0.2 },
  tabRow: { display: 'flex', gap: 8, marginBottom: 12 },

  listItem: {
    padding: '10px 12px',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
  },
  listRight: { display: 'flex', alignItems: 'center', gap: 8 },
  badge: {
    minWidth: 22,
    height: 22,
    padding: '0 6px',
    borderRadius: 12,
    background: '#ef4444',
    color: '#fff',
    fontSize: 12,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 0 rgba(0,0,0,0.08)',
  },

  searchUsers: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    marginBottom: 10,
    outline: 'none',
  },
  searchMsg: {
    width: 220,
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    outline: 'none',
  },

  messages: { }, // class handles it
  dayDivider: { alignSelf: 'center' },

  msgWrap: { display: 'flex', gap: 8, alignItems: 'flex-end' },
  msgRow: { }, // handled via .bubble
  msgMine: { }, // handled via .bubble--mine
  msgMeta: { }, // handled via .meta

  loadMoreWrap: { textAlign: 'center', margin: '6px 0' },
  loadMoreBtn: {
    fontSize: 13,
    padding: '8px 12px',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    background: '#fff',
    cursor: 'pointer',
  },
  emptyHint: { color: '#6b7280', textAlign: 'center', marginTop: 18 },

  typingRow: { height: 24, padding: '0 14px', color: '#6b7280', fontSize: 12, display:'flex', alignItems:'center' },

  // Reply UI
  replyPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    margin: '0 12px',
    borderLeft: '3px solid #60a5fa',
    background: '#f3f4f6',
    borderRadius: 12,
    boxShadow: 'var(--shadow-soft)',
  },
  replyPreviewTitle: { fontSize: 12, color: '#374151', fontWeight: 700 },
  replyPreviewText: {
    fontSize: 12,
    color: '#6b7280',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '60vw',
  },
  replyClose: {
    marginLeft: 'auto',
    border: '1px solid #e5e7eb',
    background: '#fff',
    borderRadius: 10,
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: 12,
  },
  quotedWrap: {
    borderLeft: '3px solid #93c5fd',
    background: '#eff6ff',
    padding: '8px 10px',
    borderRadius: 10,
    marginBottom: 6,
    cursor: 'pointer',
    boxShadow: 'var(--shadow-soft)',
  },
  quotedTitle: { fontSize: 11, color: '#1f2937', fontWeight: 700 },
  quotedText: { fontSize: 12, color: '#374151' },
  highlight: { boxShadow: '0 0 0 4px #fde68a inset', transition: 'box-shadow 0.6s ease' },

  inputRow: { display: 'flex', gap: 10, padding: 12, borderTop: '1px solid #e5e7eb' },
  input: {
    flex: 1,
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '12px 14px',
    resize: 'none',
    outline: 'none',
    background: '#fff',
  },

  // Action sheet
  sheetBackdrop: {
    position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.15)',
  },
  sheet: {
    position: 'fixed',
    minWidth: 260,
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    boxShadow: '0 18px 42px rgba(2,6,23,0.16)',
    padding: 8,
    zIndex: 50,
  },
  sheetMobile: {
    left: 0, right: 0, bottom: 0, borderRadius: '12px 12px 0 0',
  },
  sheetRow: { display: 'flex', gap: 8, flexWrap: 'wrap', padding: 6 },
  sheetBtn: { padding: '10px 12px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14 },
  danger: { borderColor: '#fecaca', color: '#991b1b', background: '#fff1f2' },
};

// ----- helpers -----
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const initials = (name = '') => {
  const t = name.trim();
  if (!t) return 'U';
  const parts = t.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

// Avatar chip with ring + graceful fallback
const avatarCircle = (size) => ({
  width: size,
  height: size,
  borderRadius: '50%',
  background: '#111827',
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: Math.max(11, Math.floor(size * 0.42)),
  fontWeight: 700,
  flex: `0 0 ${size}px`,
  userSelect: 'none',
  boxShadow: '0 0 0 2px #fff, 0 0 0 3px #e5e7eb',
});
const avatarImgStyle = (size) => ({
  width: size,
  height: size,
  borderRadius: '50%',
  objectFit: 'cover',
  flex: `0 0 ${size}px`,
  boxShadow: '0 0 0 2px #fff, 0 0 0 3px #e5e7eb',
});

const UserAvatar = ({ name, photoURL, size = 26 }) => {
  const [errored, setErrored] = useState(false);
  const src = !errored && photoURL ? photoURL : null;
  if (src) {
    return (
      <img
        src={src}
        alt={name || 'User'}
        style={avatarImgStyle(size)}
        onError={() => setErrored(true)}
      />
    );
  }
  return <span style={avatarCircle(size)}>{initials(name)}</span>;
};

/** ===== Linkify helper ===== */
const renderMessageText = (text = '') => {
  const urlRegex = /((https?:\/\/|www\.)[^\s<]+[^<.,:;"')\]\s])/gi;
  const nodes = [];
  let lastIndex = 0;
  let key = 0;

  const pushTextWithBreaks = (str) => {
    const parts = String(str).split('\n');
    parts.forEach((part, i) => {
      if (part) nodes.push(<span key={`t-${key++}`}>{part}</span>);
      if (i < parts.length - 1) nodes.push(<br key={`br-${key++}`} />);
    });
  };

  for (const match of String(text).matchAll(urlRegex)) {
    const start = match.index ?? 0;
    const url = match[0];

    if (start > lastIndex) pushTextWithBreaks(text.slice(lastIndex, start));

    const href = url.startsWith('http') ? url : `https://${url}`;
    nodes.push(
      <a
        key={`a-${key++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        style={{ textDecoration: 'underline', color: '#2563eb', wordBreak: 'break-all' }}
      >
        {url}
      </a>
    );
    lastIndex = start + url.length;
  }

  if (lastIndex < String(text).length) pushTextWithBreaks(text.slice(lastIndex));
  if (nodes.length === 0) pushTextWithBreaks(text);

  return nodes;
};

// === Media helpers ===
const compressImage = async (file, { maxW = 1600, maxH = 1600, quality = 0.78, format = 'image/webp' } = {}) => {
  const img = document.createElement('img');
  const url = URL.createObjectURL(file);
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });

  let { width, height } = img;
  const ratio = Math.min(maxW / width, maxH / height, 1);
  width = Math.round(width * ratio);
  height = Math.round(height * ratio);

  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise((res) => canvas.toBlob(res, format, quality));
  URL.revokeObjectURL(url);

  return new File([blob], file.name.replace(/\.(png|jpg|jpeg|heic|webp)$/i, '.webp'), { type: blob.type, lastModified: Date.now() });
};

const makeVideoThumbnail = (file, { maxW = 640, maxH = 640 } = {}) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.src = url;
  video.muted = true;
  video.playsInline = true;
  video.onloadeddata = () => {
    const seekTo = Math.min(0.5, (video.duration || 1) / 10);
    video.currentTime = seekTo;
  };
  video.onseeked = () => {
    const w = video.videoWidth;
    const h = video.videoHeight;
    const ratio = Math.min(maxW / w, maxH / h, 1);
    const cw = Math.round(w * ratio);
    const ch = Math.round(h * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = cw; canvas.height = ch;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, cw, ch);
    canvas.toBlob((blob) => {
      URL.revokeObjectURL(url);
      if (!blob) return reject(new Error('Failed to create poster'));
      const thumbFile = new File([blob], 'poster.jpg', { type: 'image/jpeg' });
      resolve(thumbFile);
    }, 'image/jpeg', 0.85);
  };
  video.onerror = reject;
});

const compressVideo = async (file, { crf = 28, maxBitrateK = 2000, scale = 720 } = {}) => {
  try {
    const { createFFmpeg, fetchFile } = await import('@ffmpeg/ffmpeg');
    const { toBlobURL } = await import('@ffmpeg/util');

    const ffmpeg = createFFmpeg({
      log: false,
      corePath: await toBlobURL(
        'https://unpkg.com/@ffmpeg/core@0.12.6/dist/ffmpeg-core.js',
        'text/javascript'
      )
    });
    if (!ffmpeg.isLoaded()) await ffmpeg.load();

    const inputName = 'input.' + (file.name.split('.').pop() || 'mp4');
    const outputName = 'output.mp4';
    ffmpeg.FS('writeFile', inputName, await fetchFile(file));

    await ffmpeg.run(
      '-i', inputName,
      '-vf', `scale='min(${scale},iw)':-2`,
      '-c:v', 'libx264', '-crf', String(crf), '-preset', 'veryfast',
      '-maxrate', `${maxBitrateK}k`, '-bufsize', `${maxBitrateK*2}k`,
      '-c:a', 'aac', '-b:a', '128k',
      outputName
    );

    const data = ffmpeg.FS('readFile', outputName);
    return new File([data.buffer], file.name.replace(/\.\w+$/, '.mp4'), { type: 'video/mp4' });
  } catch {
    return file;
  }
};

const fmtSize = (n) => (n < 1024 ? `${n} B` : n < 1024*1024 ? `${(n/1024).toFixed(1)} KB` : `${(n/1024/1024).toFixed(1)} MB`);

const ChatPage = () => {
  const { currentUser } = useAuth();
  const location = useLocation();

  const storage = getStorage();

  // responsive
  const [isMobile, setIsMobile] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  useEffect(() => {
    const onResize = () => {
      const small = window.innerWidth <= 860;
      setIsMobile(small);
      setShowSidebar(!small);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // people
  const [users, setUsers] = useState([]);
  const [searchUser, setSearchUser] = useState('');

  // chat selection
  const [activeTab, setActiveTab] = useState('global'); // 'global' | 'dm'
  const [activeChatId, setActiveChatId] = useState(GLOBAL_CHAT_ID);

  // messages + pagination
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const textRef = useRef(null);
  const listRef = useRef(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderDone, setOlderDone] = useState(false);
  const firstMessageCursorRef = useRef(null);

  // message search
  const [searchMsg, setSearchMsg] = useState('');

  // unread per chatId
  const [unreadMap, setUnreadMap] = useState({});
  const unreadSubsRef = useRef({});
  const lastReadCacheRef = useRef({});

  // typing indicators
  const [typingUsers, setTypingUsers] = useState([]);
  const typingUnsubRef = useRef(null);

  // DM seen receipt
  const [dmSeen, setDmSeen] = useState(false);

  // edit one-time
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  // action sheet state
  const [sheet, setSheet] = useState({ open: false, x: 0, y: 0, message: null });
  const longPressTimerRef = useRef(null);

  // reply-to & refs
  const [replyTarget, setReplyTarget] = useState(null);
  const messageRefs = useRef({});
  const [highlightId, setHighlightId] = useState(null);

  // deep-link ?to=
  const [pendingToId, setPendingToId] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const toFromQuery = params.get('to');
    const toFromState = location.state?.toUserId;
    const to = toFromQuery || toFromState || null;

    if (to && to !== currentUser?.uid) setPendingToId(to);
    if (!to) setPendingToId(null);
  }, [location.search, location.state, currentUser?.uid]);

  // Ensure GLOBAL chat
  useEffect(() => {
    (async () => {
      const ref = doc(db, 'chats', GLOBAL_CHAT_ID);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        await setDoc(ref, {
          type: 'global',
          createdAt: serverTimestamp(),
          lastMessage: '',
          lastMessageAt: serverTimestamp(),
        }, { merge: true });
      }
    })();
  }, []);

  // Load users
  useEffect(() => {
    const qUsers = query(collection(db, 'users'), orderBy('name', 'asc'));
    const unsub = onSnapshot(qUsers, (snap) => {
      const arr = snap.docs
        .map((d) => {
          const data = d.data() || {};
          const active = data.active !== false;
          const notTerminated = data.employmentStatus !== 'Terminated';
          if (!(active && notTerminated)) return null;
          return {
            id: d.id,
            name: data.name || data.email || 'User',
            email: data.email || '',
            photoURL: data.photoURL || null,
          };
        })
        .filter(Boolean)
        .filter((u) => u.id !== currentUser?.uid);
      setUsers(arr);
    });
    return () => unsub();
  }, [currentUser?.uid]);

  // open DM if pending
  const openDM = useCallback(
    async (otherUser) => {
      if (!currentUser?.uid || !otherUser?.id) return;
      const chatId = makeDmChatId(currentUser.uid, otherUser.id);
      setActiveTab('dm');
      setActiveChatId(chatId);
      if (isMobile) setShowSidebar(false);

      const chatRef = doc(db, 'chats', chatId);
      const snap = await getDoc(chatRef);
      if (!snap.exists()) {
        await setDoc(chatRef, {
          type: 'dm',
          members: [currentUser.uid, otherUser.id],
          createdAt: serverTimestamp(),
          lastMessage: '',
          lastMessageAt: serverTimestamp(),
        });
      }
      await markChatAsRead(chatId);
      textRef.current?.focus();
    },
    [currentUser?.uid, isMobile]
  );

  useEffect(() => {
    if (!pendingToId || !currentUser?.uid) return;
    const target = users.find((u) => u.id === pendingToId);
    if (target) {
      openDM(target);
      setPendingToId(null);
    }
  }, [pendingToId, users, currentUser?.uid, openDM]);

  // users map
  const usersById = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.id, u));
    if (currentUser) {
      m.set(currentUser.uid, {
        id: currentUser.uid,
        name: currentUser.displayName || currentUser.email || 'Me',
        email: currentUser.email || '',
        photoURL: currentUser.photoURL || null,
      });
    }
    return m;
  }, [users, currentUser]);

  // subscribe messages
  useEffect(() => {
    if (!activeChatId) return;
    setOlderDone(false);
    firstMessageCursorRef.current = null;
    setDmSeen(false);

    const qMsgs = query(
      collection(db, 'chats', activeChatId, 'messages'),
      orderBy('createdAt', 'asc'),
      limit(60)
    );
    const unsub = onSnapshot(qMsgs, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, _doc: d, ...d.data() }));
      const filtered = arr.filter((m) => !(m.hiddenBy || []).includes(currentUser?.uid));
      setMessages(filtered);
      firstMessageCursorRef.current = filtered.length ? filtered[0]._doc : null;

      requestAnimationFrame(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      });

      // DM seen
      if (activeChatId !== GLOBAL_CHAT_ID && currentUser?.uid && filtered.length) {
        const myLast = [...filtered].reverse().find((m) => m.senderId === currentUser.uid);
        if (myLast?.createdAt) {
          const [a, b] = activeChatId.split('_');
          const otherId = a === currentUser.uid ? b : a;
          const readRef = doc(db, 'chatReads', `${activeChatId}_${otherId}`);
          getDoc(readRef).then((rs) => {
            const lastReadAt = rs.exists() ? rs.data()?.lastReadAt : null;
            setDmSeen(!!(lastReadAt && lastReadAt.toMillis && myLast.createdAt?.toMillis && lastReadAt.toMillis() >= myLast.createdAt.toMillis()));
          });
        } else {
          setDmSeen(false);
        }
      } else {
        setDmSeen(false);
      }
    });

    return () => unsub();
  }, [activeChatId, currentUser?.uid]);

  // load older
  const loadOlder = useCallback(async () => {
    if (!activeChatId || loadingOlder || olderDone || !firstMessageCursorRef.current) return;
    setLoadingOlder(true);
    try {
      const olderSnap = await getDocs(
        query(
          collection(db, 'chats', activeChatId, 'messages'),
          orderBy('createdAt', 'desc'),
          startAfter(firstMessageCursorRef.current.data().createdAt || null),
          limit(60)
        )
      );
      if (olderSnap.empty) {
        setOlderDone(true);
        setLoadingOlder(false);
        return;
      }
      const older = olderSnap.docs
        .map((d) => ({ id: d.id, _doc: d, ...d.data() }))
        .reverse()
        .filter((m) => !(m.hiddenBy || []).includes(currentUser?.uid));

      const list = listRef.current;
      const prevHeight = list ? list.scrollHeight : 0;

      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        return [...older.filter((m) => !seen.has(m.id)), ...prev];
      });

      firstMessageCursorRef.current = older.length ? older[0]._doc : firstMessageCursorRef.current;

      requestAnimationFrame(() => {
        if (list) {
          const newHeight = list.scrollHeight;
          list.scrollTop = newHeight - prevHeight;
        }
      });
    } finally {
      setLoadingOlder(false);
    }
  }, [activeChatId, loadingOlder, olderDone, currentUser?.uid]);

  // title
  const activeTitle = useMemo(() => {
    if (activeChatId === GLOBAL_CHAT_ID) return 'Global Chat';
    if (!currentUser?.uid || !activeChatId.includes('_')) return 'Direct Message';
    const [a, b] = activeChatId.split('_');
    const otherId = a === currentUser.uid ? b : a;
    const other = usersById.get(otherId);
    return other?.name || 'Direct Message';
  }, [activeChatId, currentUser?.uid, usersById]);

  // filter users
  const filteredUsers = useMemo(() => {
    const s = searchUser.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) => u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s));
  }, [users, searchUser]);

  // unread
  const chatReadDocId = (chatId, uid) => `${chatId}_${uid}`;
  const markChatAsRead = useCallback(async (chatId) => {
    if (!currentUser?.uid || !chatId) return;
    const readRef = doc(db, 'chatReads', chatReadDocId(chatId, currentUser.uid));
    await setDoc(readRef, { chatId, userId: currentUser.uid, lastReadAt: serverTimestamp() }, { merge: true });
  }, [currentUser?.uid]);

  const ensureUnreadListener = useCallback((chatId) => {
    if (!currentUser?.uid || !chatId) return;

    if (unreadSubsRef.current[chatId]) {
      const { unsubRead, unsubMsgs } = unreadSubsRef.current[chatId];
      if (unsubRead) unsubRead();
      if (unsubMsgs) unsubMsgs();
      delete unreadSubsRef.current[chatId];
    }

    const readRef = doc(db, 'chatReads', chatReadDocId(chatId, currentUser.uid));
    let currentUnsubMsgs = null;

    const unsubRead = onSnapshot(readRef, (readSnap) => {
      const lastReadAt = readSnap.exists() ? readSnap.data()?.lastReadAt ?? null : null;
      lastReadCacheRef.current[chatId] = lastReadAt;

      if (currentUnsubMsgs) currentUnsubMsgs();

      let qMsgsBase = collection(db, 'chats', chatId, 'messages');
      let qMsgs;
      if (lastReadAt) {
        qMsgs = query(qMsgsBase, where('createdAt', '>', lastReadAt), orderBy('createdAt', 'asc'), limit(1000));
      } else {
        qMsgs = query(qMsgsBase, orderBy('createdAt', 'asc'), limit(1000));
      }

      currentUnsubMsgs = onSnapshot(
        qMsgs,
        (snap) => {
          let count = 0;
          snap.forEach((d) => {
            const data = d.data();
            if (data?.senderId && data.senderId !== currentUser.uid) {
              if (!((data.hiddenBy || []).includes(currentUser.uid))) count += 1;
            }
          });
          setUnreadMap((m) => ({ ...m, [chatId]: count }));
        },
        () => setUnreadMap((m) => ({ ...m, [chatId]: 0 }))
      );

      unreadSubsRef.current[chatId] = { unsubRead, unsubMsgs: currentUnsubMsgs };
    });

    unreadSubsRef.current[chatId] = { unsubRead, unsubMsgs: currentUnsubMsgs };
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    ensureUnreadListener(GLOBAL_CHAT_ID);
    filteredUsers.forEach((u) => ensureUnreadListener(makeDmChatId(currentUser.uid, u.id)));
    return () => {
      Object.values(unreadSubsRef.current).forEach(({ unsubRead, unsubMsgs }) => {
        if (unsubRead) unsubRead();
        if (unsubMsgs) unsubMsgs();
      });
      unreadSubsRef.current = {};
    };
  }, [filteredUsers, currentUser?.uid, ensureUnreadListener]);

  // typing
  const startTyping = useCallback(async (chatId) => {
    if (!currentUser?.uid) return;
    const ref = doc(db, 'typing', `${chatId}_${currentUser.uid}`);
    await setDoc(ref, {
      chatId, userId: currentUser.uid,
      userName: currentUser.displayName || currentUser.email || 'User',
      typing: true, updatedAt: serverTimestamp(),
    }, { merge: true });
  }, [currentUser?.uid, currentUser?.displayName, currentUser?.email]);

  const stopTyping = useCallback(async (chatId) => {
    if (!currentUser?.uid) return;
    const ref = doc(db, 'typing', `${chatId}_${currentUser.uid}`);
    await setDoc(ref, {
      chatId, userId: currentUser.uid,
      userName: currentUser.displayName || currentUser.email || 'User',
      typing: false, updatedAt: serverTimestamp(),
    }, { merge: true });
  }, [currentUser?.uid, currentUser?.displayName, currentUser?.email]);

  useEffect(() => {
    if (!activeChatId) return;
    if (typingUnsubRef.current) typingUnsubRef.current();

    const qTyping = query(
      collection(db, 'typing'),
      where('chatId', '==', activeChatId),
      where('typing', '==', true)
    );
    const unsub = onSnapshot(qTyping, (snap) => {
      const others = snap.docs
        .map((d) => d.data())
        .filter((x) => x.userId !== currentUser?.uid)
        .map((x) => x.userName || 'User');
      setTypingUsers(others);
    });
    typingUnsubRef.current = unsub;

    return () => {
      if (typingUnsubRef.current) typingUnsubRef.current();
      typingUnsubRef.current = null;
    };
  }, [activeChatId, currentUser?.uid]);

  // mark read
  useEffect(() => {
    if (!activeChatId) return;
    const t = setTimeout(() => markChatAsRead(activeChatId), 180);
    return () => clearTimeout(t);
  }, [messages, activeChatId, markChatAsRead]);

  // actions
  const switchToGlobal = async () => {
    setActiveTab('global');
    setActiveChatId(GLOBAL_CHAT_ID);
    if (isMobile) setShowSidebar(false);
    await markChatAsRead(GLOBAL_CHAT_ID);
    textRef.current?.focus();
  };

  // context long-press
  const openSheetFor = (m, clientX, clientY) => {
    const x = clientX ?? window.innerWidth / 2;
    const y = clientY ?? (isMobile ? window.innerHeight : window.innerHeight / 2);
    setSheet({ open: true, x, y, message: m });
  };
  const closeSheet = () => setSheet({ open: false, x: 0, y: 0, message: null });
  const onMsgMouseDown = (e, m) => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => openSheetFor(m, e.clientX, e.clientY), 420);
  };
  const onMsgMouseUp = () => { if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current); };
  const onMsgTouchStart = (e, m) => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    const t = e.touches[0];
    longPressTimerRef.current = setTimeout(() => openSheetFor(m, t.clientX, t.clientY), 420);
  };
  const onMsgTouchEnd = () => { if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current); };
  const onMsgContextMenu = (e, m) => { e.preventDefault(); openSheetFor(m, e.clientX, e.clientY); };

  // reactions
  const toggleReaction = async (m, emoji) => {
    if (!currentUser?.uid) return;
    const msgRef = doc(db, 'chats', activeChatId, 'messages', m.id);
    const has = (m.reactions?.[emoji] || []).includes(currentUser.uid);
    const path = `reactions.${emoji}`;
    await updateDoc(msgRef, { [path]: has ? arrayRemove(currentUser.uid) : arrayUnion(currentUser.uid) });
  };

  // edit
  const canEdit = (m) => m.senderId === currentUser?.uid && !m.editedAt;
  const startEdit = (m) => { if (canEdit(m)) { setEditingId(m.id); setEditText(m.text || ''); } };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };
  const saveEdit = async () => {
    if (!editingId) return;
    const ref = doc(db, 'chats', activeChatId, 'messages', editingId);
    await updateDoc(ref, { text: editText, editedAt: serverTimestamp() });
    cancelEdit();
  };

  // delete
  const deleteForMe = async (m) => {
    const ref = doc(db, 'chats', activeChatId, 'messages', m.id);
    await updateDoc(ref, { hiddenBy: arrayUnion(currentUser.uid) });
  };
  const deleteForEveryone = async (m) => {
    if (m.senderId !== currentUser?.uid) return;
    if (!window.confirm('Delete this message for everyone?')) return;
    const ref = doc(db, 'chats', activeChatId, 'messages', m.id);
    await deleteDoc(ref);
  };

  // pin
  const togglePin = async (m) => {
    const ref = doc(db, 'chats', activeChatId, 'messages', m.id);
    await updateDoc(ref, { pinned: !m.pinned });
  };

  // helpers
  const renderBadge = (n) => (n > 0 ? <span style={S.badge}>{n > 99 ? '99+' : n}</span> : null);

  const filteredMsgs = useMemo(() => {
    const s = searchMsg.trim().toLowerCase();
    if (!s) return messages;
    return messages.filter((m) => (m.text || '').toLowerCase().includes(s));
  }, [messages, searchMsg]);

  const dayLabel = (d) => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const typingText =
    typingUsers.length === 0 ? '' :
    typingUsers.length === 1 ? `${typingUsers[0]} is typing…` :
    `${typingUsers.slice(0, 2).join(', ')}${typingUsers.length > 2 ? '…' : ''} are typing…`;

  // reply jump
  const scrollToMessage = useCallback((id) => {
    const el = messageRefs.current[id];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightId(id);
      setTimeout(() => setHighlightId(null), 1600);
    }
  }, []);

  // attachments
  const [pendingFiles, setPendingFiles] = useState([]); // {file, kind, previewURL?}
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const detectKind = (file) => {
    if (!file?.type) return 'file';
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    return 'file';
  };

  const onPickFiles = (filesList) => {
    const arr = Array.from(filesList || []).slice(0, 8);
    const mapped = arr.map((f) => ({
      file: f,
      kind: detectKind(f),
      previewURL: (f.type.startsWith('image/') || f.type.startsWith('video/')) ? URL.createObjectURL(f) : null,
    }));
    setPendingFiles((prev) => [...prev, ...mapped]);
  };

  const onFileInputChange = (e) => { onPickFiles(e.target.files); e.target.value = ''; };
  const onDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); if (e.dataTransfer?.files?.length) onPickFiles(e.dataTransfer.files); };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  useEffect(() => () => { pendingFiles.forEach((p) => p.previewURL && URL.revokeObjectURL(p.previewURL)); }, []); // cleanup object URLs

  const sendAttachments = useCallback(async () => {
    if (!pendingFiles.length || !currentUser || !activeChatId) return;

    setUploading(true);
    setUploadProgress(0);
    try {
      const uploaded = [];
      for (let i = 0; i < pendingFiles.length; i++) {
        const { file, kind } = pendingFiles[i];
        let toUpload = file;

        if (kind === 'image') {
          try { toUpload = await compressImage(file); } catch { toUpload = file; }
        }
        if (kind === 'video' && file.size > 30 * 1024 * 1024) {
          try { toUpload = await compressVideo(file, { crf: 28, maxBitrateK: 2000, scale: 720 }); } catch { toUpload = file; }
        }

        // poster
        let thumbBlobFile = null;
        if (kind === 'video') {
          try { thumbBlobFile = await makeVideoThumbnail(file); } catch {}
        }

        const path = `chatUploads/${activeChatId}/${Date.now()}_${toUpload.name}`;
        const ref = storageRef(storage, path);
        const task = uploadBytesResumable(ref, toUpload, { contentType: toUpload.type });

        const pct = await new Promise((resolve, reject) => {
          task.on('state_changed',
            (snap) => {
              const p = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
              setUploadProgress(Math.min(99, p));
            },
            reject,
            () => resolve(100)
          );
        });
        setUploadProgress(pct);
        const url = await getDownloadURL(task.snapshot.ref);

        let posterURL = null;
        if (thumbBlobFile) {
          const posterPath = `chatUploads/${activeChatId}/${Date.now()}_poster_${toUpload.name}.jpg`;
          const posterRef = storageRef(storage, posterPath);
          const posterTask = uploadBytesResumable(posterRef, thumbBlobFile, { contentType: thumbBlobFile.type });
          await new Promise((res, rej) => { posterTask.on('state_changed', () => {}, rej, res); });
          posterURL = await getDownloadURL(posterRef);
        }

        uploaded.push({
          kind, url, name: toUpload.name, size: toUpload.size,
          mime: toUpload.type || 'application/octet-stream',
          posterURL: posterURL || null,
        });
      }

      const msgsRef = collection(db, 'chats', activeChatId, 'messages');
      await addDoc(msgsRef, {
        text: msgText.trim() || '',
        senderId: currentUser.uid,
        senderName: currentUser.displayName || currentUser.email || 'User',
        senderPhotoURL: currentUser.photoURL || null,
        createdAt: serverTimestamp(),
        reactions: {},
        pinned: false,
        hiddenBy: [],
        editedAt: null,
        replyTo: replyTarget ? {
          id: replyTarget.id,
          senderId: replyTarget.senderId,
          senderName: replyTarget.senderName || 'User',
          text: (replyTarget.text || '').slice(0, 300),
          createdAt: replyTarget.createdAt || null,
        } : null,
        attachments: uploaded,
      });

      const chatRef = doc(db, 'chats', activeChatId);
      const lastLine = (msgText.trim() ? msgText.trim() + ' · ' : '') + uploaded.map(a => a.kind).join(', ');
      await setDoc(chatRef, { lastMessage: lastLine.slice(0, 500), lastMessageAt: serverTimestamp() }, { merge: true });

      pendingFiles.forEach((p) => p.previewURL && URL.revokeObjectURL(p.previewURL));
      setPendingFiles([]);
      setMsgText('');
      setReplyTarget(null);
      setUploadProgress(0);
      await markChatAsRead(activeChatId);
    } finally {
      setUploading(false);
    }
  }, [pendingFiles, storage, activeChatId, currentUser, msgText, replyTarget, markChatAsRead]);

  const sendMessage = useCallback(async () => {
    if (pendingFiles.length) { await sendAttachments(); return; }
    if (!msgText.trim() || !currentUser || !activeChatId) return;

    const me = {
      uid: currentUser.uid,
      name: currentUser.displayName || currentUser.email || 'User',
      photoURL: currentUser.photoURL || null,
    };
    const text = msgText.trim();

    setMsgText('');
    stopTyping(activeChatId).catch(() => {});

    const replyTo = replyTarget ? {
      id: replyTarget.id,
      senderId: replyTarget.senderId,
      senderName: replyTarget.senderName || 'User',
      text: (replyTarget.text || '').slice(0, 300),
      createdAt: replyTarget.createdAt || null,
    } : null;

    const msgsRef = collection(db, 'chats', activeChatId, 'messages');
    await addDoc(msgsRef, {
      text,
      senderId: me.uid,
      senderName: me.name,
      senderPhotoURL: me.photoURL,
      createdAt: serverTimestamp(),
      reactions: {},
      pinned: false,
      hiddenBy: [],
      editedAt: null,
      replyTo,
    });

    const chatRef = doc(db, 'chats', activeChatId);
    await setDoc(chatRef, { lastMessage: text.slice(0, 500), lastMessageAt: serverTimestamp() }, { merge: true });

    setReplyTarget(null);
    await markChatAsRead(activeChatId);
  }, [msgText, currentUser, activeChatId, stopTyping, markChatAsRead, replyTarget, pendingFiles, sendAttachments]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && replyTarget) { setReplyTarget(null); return; }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (msgText.trim() || pendingFiles.length) sendMessage();
    }
  };

  return (
    <div className="chat-root" style={{ ...S.outer, ...(isMobile ? S.outerMobile : {}) }}>
      <DesignCSS />

      {/* Sidebar */}
      {(!isMobile || showSidebar) && (
        <aside className="card" style={S.sidebar} aria-label="Chat list">
          <div style={S.sectionTitle}>Chats</div>
          <div style={S.tabRow}>
            <button
              onClick={switchToGlobal}
              className={`tab ${activeTab === 'global' ? 'tab--active' : ''}`}
            >
              Global
            </button>
            <button
              onClick={() => setActiveTab('dm')}
              className={`tab ${activeTab === 'dm' ? 'tab--active' : ''}`}
            >
              Direct
            </button>
          </div>

          <div
            onClick={switchToGlobal}
            className={`list-item ${activeChatId === GLOBAL_CHAT_ID ? 'list-item--active' : ''}`}
            style={S.listItem}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && switchToGlobal()}
            title="Visible to all users"
          >
            <span>🌍 Global Chat</span>
            <div style={S.listRight}>{renderBadge(unreadMap[GLOBAL_CHAT_ID] || 0)}</div>
          </div>

          <div style={{ ...S.sectionTitle, marginTop: 14 }}>Users</div>
          <input
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
            placeholder="Search users…"
            style={S.searchUsers}
            aria-label="Search users"
          />
          <div>
            {filteredUsers.length === 0 && (
              <div style={{ color: '#6b7280', fontSize: 14 }}>No users.</div>
            )}
            {filteredUsers.map((u) => {
              const chatId = currentUser?.uid ? makeDmChatId(currentUser.uid, u.id) : '';
              const active = chatId && activeChatId === chatId && activeTab === 'dm';
              return (
                <div
                  key={u.id}
                  onClick={() => openDM(u)}
                  className={`list-item ${active ? 'list-item--active' : ''}`}
                  style={S.listItem}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && openDM(u)}
                  title={u.email}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <UserAvatar name={u.name} photoURL={u.photoURL} size={28} />
                    <span style={{ fontWeight: 600 }}>{u.name}</span>
                  </span>
                  <div style={S.listRight}>{renderBadge(unreadMap[chatId] || 0)}</div>
                </div>
              );
            })}
          </div>
        </aside>
      )}

      {/* Content */}
      <section className="card" style={S.content} aria-label="Chat messages">
        <div className="header-bar" style={S.header}>
          {/* Header Left */}
          <div style={S.headerTitle}>
            {activeChatId !== GLOBAL_CHAT_ID &&
              (() => {
                const [a, b] = activeChatId.split('_');
                const otherId = a === currentUser?.uid ? b : a;
                const other = usersById.get(otherId);
                return other ? (
                  <UserAvatar name={other.name} photoURL={other.photoURL} size={26} />
                ) : null;
              })()}
            <div style={S.titleText}>{activeTitle}</div>
            <span className="chip">{activeChatId === GLOBAL_CHAT_ID ? 'Public' : 'Direct'}</span>
            {activeChatId !== GLOBAL_CHAT_ID && dmSeen && (
              <span className="chip chip--seen">Seen</span>
            )}
          </div>

          {/* Header Right */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              value={searchMsg}
              onChange={(e) => setSearchMsg(e.target.value)}
              placeholder="Search in chat…"
              style={{ ...S.searchMsg, width: isMobile ? 140 : 220 }}
              aria-label="Search messages"
            />
            {isMobile && (
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowSidebar((s) => !s)}
                aria-label="Toggle chat list"
              >
                {showSidebar ? 'Hide List' : 'Show List'}
              </button>
            )}
            <button
              type="button"
              className="ghost-btn"
              onClick={() => {
                if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
                textRef.current?.focus();
              }}
            >
              Bottom
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={listRef}
          className="messages"
          style={S.messages}
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollTop < 80 && !loadingOlder && !olderDone) {
              loadOlder();
            }
          }}
        >
          {!olderDone && (
            <div style={S.loadMoreWrap}>
              <button
                type="button"
                style={S.loadMoreBtn}
                onClick={loadOlder}
                disabled={loadingOlder}
              >
                {loadingOlder ? 'Loading older…' : 'Load older messages'}
              </button>
            </div>
          )}

          {filteredMsgs.length === 0 && (
            <div style={S.emptyHint}>
              No messages yet. Long-press a message to react / reply / edit / delete. 👇
            </div>
          )}

          {filteredMsgs.map((m, idx) => {
            const mine = m.senderId === currentUser?.uid;
            const ts = m.createdAt?.toDate ? m.createdAt.toDate() : new Date();
            const prev = filteredMsgs[idx - 1];
            const prevDay = prev?.createdAt?.toDate ? prev.createdAt.toDate() : null;
            const showDivider = !prev || !prevDay || !sameDay(prevDay, ts);

            const pressHandlers = {
              onMouseDown: (e) => onMsgMouseDown(e, m),
              onMouseUp: onMsgMouseUp,
              onTouchStart: (e) => onMsgTouchStart(e, m),
              onTouchEnd: onMsgTouchEnd,
              onContextMenu: (e) => onMsgContextMenu(e, m),
            };

            const sender = usersById.get(m.senderId);
            const senderName = sender?.name || m.senderName || 'User';
            const avatarURL = m.senderPhotoURL || sender?.photoURL || null;

            return (
              <React.Fragment key={m.id}>
                {showDivider && (
                  <div style={{ ...S.dayDivider }} className="day-chip">
                    {dayLabel(ts)}
                  </div>
                )}
                <div
                  ref={(el) => (messageRefs.current[m.id] = el)}
                  style={{
                    ...S.msgWrap,
                    justifyContent: mine ? 'flex-end' : 'flex-start',
                    ...(highlightId === m.id ? S.highlight : {}),
                  }}
                >
                  {!mine && <UserAvatar name={senderName} photoURL={avatarURL} size={28} />}

                  <div
                    className={`bubble ${mine ? 'bubble--mine' : ''}`}
                    {...pressHandlers}
                  >
                    {editingId === m.id ? (
                      <>
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={3}
                          style={{
                            width: '100%',
                            border: '1px solid #e5e7eb',
                            borderRadius: 10,
                            padding: 10,
                            outline: 'none',
                          }}
                        />
                        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                          <button className="ghost-btn" onClick={saveEdit}>
                            Save
                          </button>
                          <button className="ghost-btn" onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Quoted (replyTo) preview */}
                        {m.replyTo && (
                          <div
                            style={S.quotedWrap}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (m.replyTo?.id) scrollToMessage(m.replyTo.id);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            title="Jump to original"
                          >
                            <div style={S.quotedTitle}>
                              Replying to {m.replyTo.senderName || 'User'}
                            </div>
                            <div style={S.quotedText}>
                              {m.replyTo.text
                                ? m.replyTo.text.length > 140
                                  ? `${m.replyTo.text.slice(0, 140)}…`
                                  : m.replyTo.text
                                : '(message)'}
                            </div>
                          </div>
                        )}

                        {m.pinned && <span className="chip chip--pinned">📌 Pinned</span>}

                        {/* Attachments */}
                        {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                          <div style={{ display: 'grid', gap: 8, marginBottom: 6 }}>
                            {m.attachments.map((a, i) => {
                              if (a.kind === 'image') {
                                return (
                                  <a key={i} href={a.url} target="_blank" rel="noreferrer" onClick={(e)=>e.stopPropagation()}>
                                    <img
                                      src={a.url}
                                      alt={a.name || 'image'}
                                      style={{
                                        maxWidth: 380,
                                        borderRadius: 12,
                                        display: 'block',
                                        border: '1px solid #e5e7eb',
                                        boxShadow: 'var(--shadow-soft)',
                                      }}
                                    />
                                  </a>
                                );
                              }
                              if (a.kind === 'video') {
                                return (
                                  <div key={i} style={{ maxWidth: 420 }}>
                                    <video
                                      controls
                                      poster={a.posterURL || undefined}
                                      style={{
                                        width: '100%',
                                        borderRadius: 12,
                                        display: 'block',
                                        border: '1px solid #e5e7eb',
                                        boxShadow: 'var(--shadow-soft)',
                                      }}
                                    >
                                      <source src={a.url} type={a.mime || 'video/mp4'} />
                                    </video>
                                    <div className="meta" style={{ marginTop: 6 }}>
                                      {a.name} · {fmtSize(a.size)}
                                    </div>
                                  </div>
                                );
                              }
                              // generic file
                              return (
                                <div
                                  key={i}
                                  style={{
                                    padding: 10,
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 12,
                                    background: '#f9fafb',
                                    boxShadow: 'var(--shadow-soft)',
                                  }}
                                >
                                  <a
                                    href={a.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e)=>e.stopPropagation()}
                                    style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 600 }}
                                  >
                                    {a.name || 'file'}
                                  </a>
                                  <div className="meta" style={{ marginTop: 4 }}>
                                    {a.mime || 'file'} · {fmtSize(a.size)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div>{renderMessageText(m.text)}</div>

                        <div className="meta">
                          <span>{ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          {m.editedAt && <span>(edited)</span>}
                          {Object.entries(m.reactions || {}).map(([emoji, arr]) =>
                            arr && arr.length > 0 ? (
                              <span key={emoji} className="chip">
                                {emoji} {arr.length}
                              </span>
                            ) : null
                          )}
                        </div>
                        <div className="hint">Long-press for actions</div>
                      </>
                    )}
                  </div>

                  {mine && <UserAvatar name={senderName} photoURL={avatarURL} size={28} />}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Typing indicator */}
        <div style={S.typingRow}>
          {typingText}
        </div>

        {/* Reply preview */}
        {replyTarget && (
          <div
            style={S.replyPreview}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <div style={S.replyPreviewTitle}>Replying to {replyTarget.senderName || 'User'}</div>
              <div style={S.replyPreviewText}>
                {replyTarget.text?.length > 140 ? `${replyTarget.text.slice(0, 140)}…` : replyTarget.text}
              </div>
            </div>
            <button
              type="button"
              style={S.replyClose}
              onClick={() => setReplyTarget(null)}
              aria-label="Cancel reply"
            >
              ✕
            </button>
          </div>
        )}

        {/* Input (with uploader + drag&drop) */}
        <div
          className={`drop-zone ${dragOver ? 'drop-zone--drag' : ''}`}
          style={{ ...S.inputRow, borderRadius: 12 }}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
        >
          {/* Attach */}
          <button
            type="button"
            className="ghost-btn"
            onClick={() => fileInputRef.current?.click()}
            aria-label="Attach files"
            disabled={uploading}
            title="Attach files"
          >
            📎 Attach
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
            style={{ display: 'none' }}
            onChange={onFileInputChange}
          />

          <textarea
            ref={textRef}
            value={msgText}
            onChange={(e) => {
              setMsgText(e.target.value);
              if (e.target.value.trim()) startTyping(activeChatId);
              else stopTyping(activeChatId);
            }}
            onBlur={() => stopTyping(activeChatId)}
            onKeyDown={handleKeyDown}
            placeholder={
              replyTarget
                ? 'Write a reply…'
                : 'Type a message… (Enter to send, Shift+Enter for newline, drag files to attach)'
            }
            style={S.input}
            rows={2}
            aria-label="Message input"
            disabled={uploading}
          />

          <button
            className="primary-btn"
            onClick={sendMessage}
            disabled={!(msgText.trim() || pendingFiles.length) || uploading}
          >
            {uploading ? 'Uploading…' : 'Send'}
          </button>
        </div>

        {/* Pending attachments + progress */}
        {(pendingFiles.length > 0 || uploading) && (
          <div style={{ padding: '0 12px 12px' }}>
            {pendingFiles.length > 0 && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {pendingFiles.map((p, i) => (
                  <div
                    key={i}
                    className="card"
                    style={{ padding: 10, borderRadius: 12, background:'#fff' }}
                  >
                    <div style={{ fontSize: 12, color: '#374151', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {p.file.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{p.file.type || 'file'} · {fmtSize(p.file.size)}</div>
                    {p.previewURL && p.kind === 'image' && (
                      <img src={p.previewURL} alt="" style={{ width: 180, height: 'auto', borderRadius: 10, marginTop: 8, border:'1px solid #e5e7eb' }} />
                    )}
                    {p.previewURL && p.kind === 'video' && (
                      <video src={p.previewURL} style={{ width: 200, borderRadius: 10, marginTop: 8, border:'1px solid #e5e7eb' }} muted controls />
                    )}
                    <div style={{ marginTop: 8, display:'flex', gap:8 }}>
                      <button
                        type="button"
                        className="ghost-btn"
                        style={{ padding: '6px 10px', fontSize: 12 }}
                        onClick={() => {
                          setPendingFiles((prev) => {
                            const copy = [...prev];
                            const removed = copy.splice(i, 1)[0];
                            if (removed?.previewURL) URL.revokeObjectURL(removed.previewURL);
                            return copy;
                          });
                        }}
                        disabled={uploading}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {uploading && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 6, display:'flex', justifyContent:'space-between' }}>
                  <span>Uploading…</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="progress">
                  <span style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Action Sheet */}
      {sheet.open && sheet.message && (
        <>
          <div style={S.sheetBackdrop} onClick={closeSheet} />
          <div
            style={{
              ...S.sheet,
              ...(isMobile
                ? { ...S.sheetMobile }
                : { left: Math.min(sheet.x, window.innerWidth - 280), top: Math.min(sheet.y, window.innerHeight - 300) }),
            }}
            role="dialog"
            aria-modal="true"
          >
            <div style={{ fontWeight: 800, padding: '4px 8px' }}>Quick Reactions</div>
            <div style={S.sheetRow}>
              {['👍', '❤️', '😂', '🎉', '😮', '🙏'].map((e) => (
                <button
                  key={e}
                  className="ghost-btn"
                  style={S.sheetBtn}
                  onClick={() => { toggleReaction(sheet.message, e); closeSheet(); }}
                >
                  {e}
                </button>
              ))}
            </div>

            <div style={{ fontWeight: 800, padding: '4px 8px', marginTop: 4 }}>Actions</div>
            <div style={S.sheetRow}>
              <button
                className="ghost-btn"
                style={S.sheetBtn}
                onClick={() => {
                  const m = sheet.message;
                  setReplyTarget({
                    id: m.id,
                    senderId: m.senderId,
                    senderName: m.senderName,
                    text: m.text,
                    createdAt: m.createdAt || null,
                  });
                  closeSheet();
                }}
              >
                Reply
              </button>

              {canEdit(sheet.message) && (
                <button
                  className="ghost-btn"
                  style={S.sheetBtn}
                  onClick={() => { closeSheet(); startEdit(sheet.message); }}
                >
                  Edit (once)
                </button>
              )}

              <button
                className="ghost-btn"
                style={S.sheetBtn}
                onClick={() => { togglePin(sheet.message); closeSheet(); }}
              >
                {sheet.message.pinned ? 'Unpin' : 'Pin'}
              </button>

              <button
                className="ghost-btn"
                style={S.sheetBtn}
                onClick={() => { deleteForMe(sheet.message); closeSheet(); }}
              >
                Delete for me
              </button>

              {sheet.message.senderId === currentUser?.uid && (
                <button
                  className="ghost-btn"
                  style={{ ...S.sheetBtn, ...S.danger }}
                  onClick={() => { deleteForEveryone(sheet.message); closeSheet(); }}
                >
                  Delete for everyone
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatPage;
